/**
 * MAIN APP COMPONENT
 * Handles routing between Login and Dashboard based on authentication state
 */

import { ErrorBoundary } from './components/ErrorBoundary'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { LoginPage } from './pages/Login'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { DashboardHome } from './pages/dashboard/Home'
import { PartnersCompletePage } from './pages/dashboard/PartnersComplete'
import { ClientsPage } from './pages/dashboard/Clients'
import { TransactionsPage } from './pages/dashboard/Transactions'
import { PoolBalancePage } from './pages/dashboard/PoolBalance'
import { DistributionsPage } from './pages/dashboard/Distributions'
import { WiseSyncPage } from './pages/dashboard/WiseSync'
import { MyPortalPage } from './pages/dashboard/MyPortal'
import { AdvancesPage } from './pages/dashboard/Advances'
import { ReportsPage } from './pages/dashboard/Reports'
import { SettingsPage } from './pages/dashboard/Settings'
import { ReservesPage } from './pages/dashboard/Reserves'
import { MonthlyClosingPage } from './pages/dashboard/MonthlyClosing'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-bg-primary via-light-bg-secondary to-light-bg-tertiary dark:from-dark-bg-primary dark:via-dark-bg-secondary dark:to-dark-bg-tertiary flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue to-green rounded-2xl animate-pulse mx-auto mb-4 flex items-center justify-center">
            <span className="text-3xl">💰</span>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary font-medium">Loading...</p>
        </div>
      </div>
    )
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />
}

export function App() {
  const { user, isLoading } = useAuth()

  console.log('App render - user:', user, 'isLoading:', isLoading)

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<ErrorBoundary><DashboardHome /></ErrorBoundary>} />
            <Route path="partners" element={<ErrorBoundary><PartnersCompletePage /></ErrorBoundary>} />
            <Route path="clients" element={<ErrorBoundary><ClientsPage /></ErrorBoundary>} />
            <Route path="transactions" element={<ErrorBoundary><TransactionsPage /></ErrorBoundary>} />
            <Route path="pool" element={<ErrorBoundary><PoolBalancePage /></ErrorBoundary>} />
            <Route path="distributions" element={<ErrorBoundary><DistributionsPage /></ErrorBoundary>} />
            <Route path="wise" element={<ErrorBoundary><WiseSyncPage /></ErrorBoundary>} />
            <Route path="my-portal" element={<ErrorBoundary><MyPortalPage /></ErrorBoundary>} />
            <Route path="advances" element={<ErrorBoundary><AdvancesPage /></ErrorBoundary>} />
            <Route path="reports" element={<ErrorBoundary><ReportsPage /></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
            <Route path="reserves" element={<ErrorBoundary><ReservesPage /></ErrorBoundary>} />
            <Route path="monthly-closing" element={<ErrorBoundary><MonthlyClosingPage /></ErrorBoundary>} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
